import { WorkflowConfig } from "@p0security/shared/types/workflow/types";
import { message } from "antd";
import { useAuthFetch } from "components/Login/hook";
import yaml from "js-yaml";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useRoutingRulesStore } from "store/routingRulesStore";

export const useSubmitWorkflow = () => {
  const {
    setCurrentVersion,
    setErrors,
    setJsonRules,
    setIsSubmitting,
    setYamlRules,
    setShowConfigRulesModal,
  } = useRoutingRulesStore();

  const setSingleError = useCallback(
    (error: string | undefined) => setErrors(error ? [error] : []),
    [setErrors]
  );

  const authFetch = useAuthFetch(setSingleError);
  const [_, setSearchParams] = useSearchParams();
  return useCallback(
    async ({
      jsonData,
      shouldNavigate = true,
      successMessage = "Workflow successfully saved.",
    }: {
      // TODO: move to shared types
      jsonData: {
        currentVersion: string | undefined;
        workflow: WorkflowConfig;
      };
      shouldNavigate: boolean;
      successMessage?: string;
    }) => {
      setIsSubmitting(true);
      try {
        const response = await authFetch("routing", {
          method: "POST",
          json: jsonData,
          onNotOk: async (response) => {
            if (response.status === 400) {
              setIsSubmitting(false);
              const data = await response.json();
              setErrors(
                data.errorState?.errors?.map(
                  (e: { error: string }) => `${e.error}`
                ) ?? [data.error ?? String(data)]
              );
            } else if (response.status > 400) {
              setIsSubmitting(false);
              try {
                const { error } = await response.json();
                setErrors([error ?? "An unknown server error occurred."]);
              } catch (parseErr) {
                console.error("Failed to parse error response:", parseErr);
                setErrors([
                  "Saving workflow failed. You may try again, or, if this problem persists, please contact support@p0.dev.",
                ]);
              }
            }
          },
        });
        if (!response) return;
        const {
          workflow: { rules, version },
        } = await response.json();
        setCurrentVersion(version);
        setJsonRules(rules);
        setYamlRules(yaml.dump(rules, { flowLevel: 4 }));
        setSearchParams({});
        message.success(successMessage);

        setIsSubmitting(false);
        setErrors([]);
        if (shouldNavigate) {
          setShowConfigRulesModal(false);
        }
      } catch (err) {
        setIsSubmitting(false);
        setErrors([String(err)]);
      }
    },
    [
      authFetch,
      setCurrentVersion,
      setErrors,
      setIsSubmitting,
      setJsonRules,
      setShowConfigRulesModal,
      setYamlRules,
      setSearchParams,
    ]
  );
};
